import type { MapDispatchToPropsFunction, MapStateToProps } from 'react-redux';
import { connect } from 'react-redux';
import type { TabOwnProps } from 'shared/connect/connect';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import type { DataById, UpdateAction, UpdateActionById } from 'state/formData';
import type { ProductNode } from 'state/product-description/model';
import {
  getAllSelectedProductNodesByTabId,
  getBatchProductSubType,
  getBatchProductType,
  getMergedFieldsMetadataByTabId,
  getMultipleBatchApplyFieldsActions,
  getMultipleBatchApplyFieldsData,
  getMultipleBatchApplySelectedFieldsIds,
  getSelectedFormIdsCountByBatchTabId,
  isMultipleApplyApplyButtonDisabled,
  isMultipleApplyOpened,
} from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import {
  batchMultipleApplyClearFieldValue,
  batchMultipleApplySetFieldAction,
  batchMultipleApplySetFieldValue,
  batchMultipleApplySetSelectedFields,
  toggleBatchWorkspaceMultipleApply,
} from 'state/workspaces/batch/tabContent/tabContent.actions';
import { applyMultipleBatchThunk } from 'state/workspaces/batch/tabContent/tabContent.thunks';

export type MultipleApplyStateProps = {
  isMultipleApplyOpened: boolean;
  fieldsMetadata: FieldMetadataById;
  fieldsNodes: readonly ProductNode[];
  fieldsData: DataById;
  formsCnt: number;
  selectedFieldsIds: readonly string[];
  mainProductType?: string;
  mainProductSubtype?: string;
  fieldsActions: UpdateActionById;
  isApplyButtonDisabled: boolean;
};
export type MultipleApplyDispatchProps = {
  onApply: () => void;
  onClose: () => void;
  setFieldValue: (field: string, value: unknown) => void;
  setSelectedFieldsIds: (fieldsIds: readonly string[]) => void;
  setFieldAction: (field: string, action: UpdateAction) => void;
};
export type MultipleApplyOwnProps = TabOwnProps;

export type MultipleApplyProps = MultipleApplyStateProps & MultipleApplyDispatchProps & MultipleApplyOwnProps;

export const mapStateToProps: MapStateToProps<MultipleApplyStateProps, MultipleApplyOwnProps, AppState> = (
  state: AppState,
  { tabId }: MultipleApplyOwnProps,
): MultipleApplyStateProps => {
  const fieldsMetadata = getMergedFieldsMetadataByTabId(state, tabId);
  return {
    isMultipleApplyOpened: isMultipleApplyOpened(state, tabId),
    fieldsMetadata,
    formsCnt: getSelectedFormIdsCountByBatchTabId(state, tabId),
    fieldsNodes: getAllSelectedProductNodesByTabId(state, tabId),
    selectedFieldsIds: getMultipleBatchApplySelectedFieldsIds(state, tabId),
    fieldsData: getMultipleBatchApplyFieldsData(state, tabId),
    fieldsActions: getMultipleBatchApplyFieldsActions(state, tabId),
    mainProductType: getBatchProductType(state, tabId),
    mainProductSubtype: getBatchProductSubType(state, tabId),
    isApplyButtonDisabled: isMultipleApplyApplyButtonDisabled(state, tabId),
  };
};

export const mapDispatchToProps: MapDispatchToPropsFunction<MultipleApplyDispatchProps, MultipleApplyOwnProps> = (
  dispatch: ThunkDispatch,
  { tabId }: MultipleApplyOwnProps,
): MultipleApplyDispatchProps => ({
  onApply() {
    logAnalytics.trackEvent('Batch Workspace', 'Apply batch apply');
    dispatch(applyMultipleBatchThunk(tabId));
    dispatch(toggleBatchWorkspaceMultipleApply(tabId, false));
    dispatch(batchMultipleApplyClearFieldValue(tabId));
  },
  onClose() {
    logAnalytics.trackEvent('Batch Workspace', 'Cancel batch apply');
    dispatch(toggleBatchWorkspaceMultipleApply(tabId, false));
  },
  setFieldValue(field, value) {
    dispatch(batchMultipleApplySetFieldValue(tabId, field, value as Readonly<unknown>));
  },
  setFieldAction(field, action) {
    dispatch(batchMultipleApplySetFieldAction(tabId, field, action));
  },
  setSelectedFieldsIds(fieldsIds) {
    dispatch(batchMultipleApplySetSelectedFields(tabId, fieldsIds));
  },
});

export const connectMultipleApply = connect<
  MultipleApplyStateProps,
  MultipleApplyDispatchProps,
  MultipleApplyOwnProps,
  AppState
>(mapStateToProps, mapDispatchToProps);
